/**
 * Internal Knowledge API
 * =======================
 * API functions for internal knowledge management
 */

import apiClient from './client';

const IK_BASE = '/api/v1/internal-knowledge';

/**
 * Get all internal knowledge categories with counts
 */
export const getIKCategories = async () => {
  const response = await apiClient.get(`${IK_BASE}/categories`);
  return response.data;
};

/**
 * Get internal knowledge entries
 * @param {string|null} category - Optional category filter
 */
export const getIKEntries = async (category = null) => {
  const params = category ? { category } : {};
  const response = await apiClient.get(`${IK_BASE}/entries`, { params });
  return response.data;
};

/**
 * Create a new internal knowledge entry
 * @param {Object} entryData - Entry data
 */
export const createIKEntry = async (entryData) => {
  const response = await apiClient.post(`${IK_BASE}/entries`, entryData);
  return response.data;
};

/**
 * Update an internal knowledge entry
 * @param {string} entryId - Entry UUID
 * @param {Object} updateData - Fields to update
 */
export const updateIKEntry = async (entryId, updateData) => {
  const response = await apiClient.put(`${IK_BASE}/entries/${entryId}`, updateData);
  return response.data;
};

/**
 * Delete an internal knowledge entry (soft delete)
 * @param {string} entryId - Entry UUID
 */
export const deleteIKEntry = async (entryId) => {
  const response = await apiClient.delete(`${IK_BASE}/entries/${entryId}`);
  return response.data;
};

/**
 * Get version history for an IK entry
 * @param {string} entryId - Entry UUID
 */
export const getIKEntryHistory = async (entryId) => {
  const response = await apiClient.get(`${IK_BASE}/entries/${entryId}/history`);
  return response.data;
};

/**
 * Revert an IK entry to a prior version
 * @param {string} entryId - Entry UUID
 * @param {number} versionNumber - Version number to revert to
 */
export const revertIKEntry = async (entryId, versionNumber) => {
  const response = await apiClient.post(`${IK_BASE}/entries/${entryId}/revert`, {
    version_number: versionNumber,
  });
  return response.data;
};

const internalKnowledgeAPI = {
  getIKCategories,
  getIKEntries,
  createIKEntry,
  updateIKEntry,
  deleteIKEntry,
  getIKEntryHistory,
  revertIKEntry,
};

export default internalKnowledgeAPI;
